import { ClientDTO, DncDetailsDTO, ICustomField, IDncByTeam, ILocation } from 'wisestep-api';
import { FieldOption } from 'wisestep-base';
import { AnySchema, array, boolean, date, mixed, number, object, string } from 'yup';

import { INameAndId } from '../../../features/jobs/assign/types';
import {
  linkedInRegex,
  phoneNumberRegex,
} from '../../clientListingPage/helper/ClientListingHelper';

export interface IFilters {
  search: string;
  status: string[] | null;
  clientNameAndIds: INameAndId[];
  tags?: string[];
  clients: ClientDTO[];
  isMyContact?: boolean;
  contactOwners?: string[];
  createdByIds?: string[];
  fromDate?: string;
  toDate?: string;
  location?: ILocation | null;
  supervisors?: string[];
  filterByDateType?: string;
  customFieldFilters?: Record<string, any>;
  entityType?: string[];
}

export const contactsDefaultFilterValues = {
  search: '',
  status: [],
  clientNameAndIds: [],
  tags: [],
  clients: [],
  contactOwners: [],
  createdByIds: [],
  fromDate: '',
  toDate: '',
  location: null,
  supervisors: [],
  filterByDateType: '',
  customFieldFilters: {},
  entityType: ['PROSPECT'],
};

export const defaultColumnSettings = (): FieldOption[] => [
  { id: 'firstName', checked: true, required: true },
  { id: 'clientName', checked: true },
  { id: 'markStatus', checked: true },
  { id: 'tags', checked: true },
  { id: 'activeLeadCount', checked: true },
  { id: 'position', checked: false },
  { id: 'emailAddress', checked: false },
  { id: 'owner', checked: true },
  { id: 'location', checked: false },
  { id: 'phoneNumber', checked: false },
  { id: 'createdByName', checked: false },
  { id: 'createdOn', checked: true },
  { id: 'action', checked: true, required: true },
];

export function mapDncDetailsDtoToIdncByTeam(dto: DncDetailsDTO): IDncByTeam {
  return {
    dataPrivacyOption: dto.dataPrivacyOption,
    dncEndDate: new Date(dto.dncEndDate),
    notes: dto.notes,
    isInDnc: dto.isInDnc,
    isAddedByCandidate: dto.isAddedByCandidate,
    createdOn: dto.createdOn ? new Date(dto.createdOn) : new Date(),
    createdBy: dto.createdBy ?? '',
    candidateId: '',
    id: '',
    teamIds: [],
    recruiterId: '',
    dncLogId: '',
    modifiedOn: new Date(),
    modifiedBy: '',
    acceptedOn: new Date(),
    deleteRequestAcceptedBy: '',
    isDeleted: false,
  };
}

export const isFieldRequired = (
  fieldName: string,
  customFields: ICustomField[] | null
): boolean => {
  const requiredFromDb = customFields?.find((f) => f.name === fieldName)?.required;
  return requiredFromDb ?? DEFAULT_REQUIRED_FIELD_NAMES.includes(fieldName);
};

const DEFAULT_REQUIRED_FIELD_NAMES = ['firstName', 'emailAddress', 'linkedinProfile', 'ownerId'];

export const buildClientContactValidationSchema = (customFields: ICustomField[] | null) => {
  const isFieldRequired = (fieldName: string): boolean => {
    const fromDb = customFields?.find((f) => f.name === fieldName && f.visible !== false);
    return fromDb?.required ?? DEFAULT_REQUIRED_FIELD_NAMES.includes(fieldName);
  };
  const baseShape: Record<string, AnySchema> = {
    firstName: isFieldRequired('firstName')
      ? string().required('Please provide First Name')
      : string(),
    emailAddress: isFieldRequired('emailAddress')
      ? string().required('Client contact Email is required').email('Email is invalid')
      : string().email('Email is invalid'),
    linkedinProfile: string().when([], {
      is: () => isFieldRequired('linkedinProfile'),
      then: (schema) =>
        schema.required('Please provide a valid LinkedIn URL').matches(linkedInRegex, {
          excludeEmptyString: true,
          message: 'Please provide a valid LinkedIn URL',
        }),
      otherwise: (schema) =>
        schema.nullable().notRequired().matches(linkedInRegex, {
          excludeEmptyString: true,
          message: 'Please provide a valid LinkedIn URL',
        }),
    }),
    phoneNumber: isFieldRequired('phoneNumber')
      ? string().required('Please provide a valid phone number').matches(phoneNumberRegex, {
          excludeEmptyString: true,
          message: 'Please provide a valid phone number',
        })
      : string().nullable().notRequired().matches(phoneNumberRegex, {
          excludeEmptyString: true,
          message: 'Please provide a valid phone number',
        }),
    ownerId: isFieldRequired('ownerId')
      ? string().required('Please provide Client Contact Owner')
      : string(),
    lastName: isFieldRequired('lastName')
      ? string().required('Please provide last name')
      : string(),
    location: isFieldRequired('location')
      ? object()
          .nullable()
          .test('not-empty', 'Please provide location', function (value) {
            if (!value) return false;
            return Object.keys(value).length > 0;
          })
          .required('Please provide location')
      : object().nullable(),
    markStatus: isFieldRequired('markStatus')
      ? string().required('Please provide mark status')
      : string().nullable(),
    supervisorEmailAddress: isFieldRequired('supervisorEmailAddress')
      ? string().required('Please provide supervisor')
      : string().nullable(),
    position: isFieldRequired('position') ? string().required('Please provide position') : string(),
    tags: isFieldRequired('tags')
      ? array()
          .of(string())
          .nullable()
          .test('not-empty', 'Please provide at least one tag', function (value) {
            return Array.isArray(value) && value.length > 0;
          })
          .required('Please provide at least one tag')
      : array().of(string()).nullable(),
  };
  const customFieldsShape: Record<string, AnySchema> = {};
  customFields?.forEach((field) => {
    if (
      field.visible === false ||
      DEFAULT_REQUIRED_FIELD_NAMES.includes(field.name ?? '') ||
      field.isDefault === true
    ) {
      return;
    }
    let schema: AnySchema;
    switch (field.type) {
      case 'TextBox':
        schema = string();
        break;
      case 'Dropdown':
        schema = field.multiple ? array().of(string()) : string();
        break;
      case 'Tags':
        schema = array().of(string());
        break;
      case 'Number':
        schema = number().typeError(`${field.label || field.name} must be a number`);
        break;
      case 'Checkbox':
        if (Array.isArray(field.options) && field.options.length > 0) {
          schema = string().oneOf(field.options, `${field.label || field.name} is required`);
        } else {
          schema = boolean().transform((value, originalValue) => {
            if (typeof originalValue === 'string') {
              return (
                originalValue.toLowerCase() === 'yes' || originalValue.toLowerCase() === 'true'
              );
            }
            return originalValue;
          });
        }
        break;
      case 'Date':
        schema = date().typeError(`${field.label || field.name} must be a date`);
        break;
      case 'URL':
        schema = string().matches(linkedInRegex, {
          message: 'Please provide a valid URL',
          excludeEmptyString: true,
        });
        break;
      case 'Location':
        schema = mixed();
        break;
      default:
        schema = mixed();
    }
    schema = field.required
      ? schema.required(`${field.label || field.name} is required`)
      : schema.notRequired().nullable();
    customFieldsShape[field.name ?? ''] = schema;
  });
  return object().shape({
    ...baseShape,
    customFields: object().shape(customFieldsShape).nullable(),
  });
};

export type BulkUpdateType = 'owner' | 'status' | 'entityType' | 'dnc' | 'assignUsers';

export const titles: Record<BulkUpdateType, string> = {
  owner: 'Bulk Update Owner',
  status: 'Bulk Update Status',
  dnc: 'Bulk Update DNC Details',
  assignUsers: 'Bulk Assign Users',
  entityType: 'Bulk Update',
};

export const labels: Record<BulkUpdateType, string> = {
  owner: 'Contact Owner',
  status: 'Contact Status',
  dnc: 'DNC Details',
  assignUsers: 'Assigned Users',
  entityType: '',
};

export interface BulkUpdateAction {
  type: string;
  label: string;
}

export const BULK_UPDATE_ACTIONS: { type: BulkUpdateType; label: string }[] = [
  { type: 'dnc', label: 'Mark DNC' },
  { type: 'status', label: 'Update Status' },
  { type: 'owner', label: 'Update Owner' },
  { type: 'assignUsers', label: 'Assign Users' },
];
